import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';

import { BreakpointService } from './breakpoint.service';
import { BREAKPOINTS, Breakpoints, SSR_OPTIONS, SsrOptions } from './breakpoints-config';

/**
 * Module that provides different utils for breakpoints
 *
 * You should configure the module using the .withConfiguration method.
 */
@NgModule({
    providers: [BreakpointService]
})
export class BreakpointModule {
    constructor(
        @Optional()
        @SkipSelf()
        parentModule: BreakpointModule
    ) {
        if (parentModule) {
            throw new Error('BreakpointModule is already loaded. Import it in the AppModule only');
        }
    }

    /**
     * Creates a module with a configuration object for the breakpoint provider
     *
     * Breakpoints (see `Breakpoint`)
     *
     * ```typescript
     * const defaultBreakpoints: Breakpoints = [
     *     Breakpoint.fromWidth('mobile', { maxWidth: 567 }),
     *     Breakpoint.fromWidth('tablet', { minWidth: 568, maxWidth: 1024 }),
     *     Breakpoint.fromQuery('desktop', 'only screen AND (min-width: 1025px)')
     * ];
     * ```
     *
     * SsrOptions (see `SsrOptions`):
     * `defaultSize` makes the router log all its internal events to the console.
     * `width` fallback width (pixels) when in server-side mode (default: 375)
     * `height` fallback height (pixels) when in server-side mode (default: 667)
     * @param breakpoints
     * @param ssrOptions
     */
    static withConfiguration(breakpoints: Breakpoints, ssrOptions?: SsrOptions): ModuleWithProviders<any> {
        return {
            ngModule: BreakpointModule,
            providers: [
                BreakpointService,
                {
                    provide: BREAKPOINTS,
                    useValue: breakpoints
                },
                {
                    provide: SSR_OPTIONS,
                    useValue: ssrOptions || null
                }
            ]
        };
    }
}
